import React, { useState, useEffect, useCallback } from 'react';
import { LoadingSpinner } from '../../../components/layout/SharedComponents';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Save, Copy, ExternalLink, Info, Bold, Italic, Underline, List, ListOrdered, Link2, Undo, Redo } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import LinkExtension from '@tiptap/extension-link';

const DEFAULT_PORTAL_TEXT = `<p>To participate, you must be a registered and approved supplier. If you are not yet registered, then please <a href="/supplier-portal">register here</a> and the system will guide you through the registration process.</p>
<p>Once approved, you can view event details, download documentation, and submit your response or bid.</p>
<p>To get started, select an event from the listings below by clicking any of the RFx lines below.</p>`;

function RichTextToolbar({ editor }) {
  if (!editor) return null;
  const btn = (active, onClick, Icon, title) => (
    <button onClick={onClick} title={title}
      className={`p-1.5 rounded hover:bg-slate-200 transition-colors ${active ? 'bg-slate-200 text-indigo-700' : 'text-slate-500'}`}>
      <Icon className="w-4 h-4" />
    </button>
  );
  return (
    <div className="flex items-center gap-0.5 px-2 py-1.5 border-b bg-slate-50">
      {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), Bold, 'Bold')}
      {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), Italic, 'Italic')}
      {btn(false, () => editor.chain().focus().toggleUnderline?.().run(), Underline, 'Underline')}
      <div className="w-px h-5 bg-slate-200 mx-1" />
      {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), List, 'Bullet list')}
      {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), ListOrdered, 'Numbered list')}
      <div className="w-px h-5 bg-slate-200 mx-1" />
      {btn(editor.isActive('link'), () => {
        if (editor.isActive('link')) { editor.chain().focus().unsetLink().run(); return; }
        const url = window.prompt('Enter URL:', 'https://');
        if (url) editor.chain().focus().setLink({ href: url }).run();
      }, Link2, 'Link')}
      <div className="w-px h-5 bg-slate-200 mx-1" />
      {btn(false, () => editor.chain().focus().undo().run(), Undo, 'Undo')}
      {btn(false, () => editor.chain().focus().redo().run(), Redo, 'Redo')}
    </div>
  );
}

export function RFQSettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      LinkExtension.configure({ openOnClick: false, HTMLAttributes: { class: 'text-indigo-700 underline font-semibold' } }),
    ],
    content: DEFAULT_PORTAL_TEXT,
    onUpdate: ({ editor: e }) => {
      setSettings(p => p ? { ...p, rfx_portal_text: e.getHTML() } : p);
    },
  });

  useEffect(() => { fetchSettings(); }, []); // eslint-disable-line

  const fetchSettings = async () => {
    try {
      const res = await api.get('/api/admin/rfq-settings');
      const s = res.data.settings || getDefaultSettings();
      setSettings(s);
      if (editor && s.rfx_portal_text) {
        editor.commands.setContent(s.rfx_portal_text);
      }
    } catch {
      setSettings(getDefaultSettings());
    }
    setLoading(false);
  };

  // Update editor when settings load
  useEffect(() => {
    if (editor && settings?.rfx_portal_text && !editor.isFocused) {
      const current = editor.getHTML();
      if (current !== settings.rfx_portal_text && settings.rfx_portal_text !== DEFAULT_PORTAL_TEXT) {
        editor.commands.setContent(settings.rfx_portal_text);
      }
    }
  }, [editor, settings?.rfx_portal_text]); // eslint-disable-line

  const getDefaultSettings = () => ({
    activate_rfx_limit: false,
    rfx_required_limit: 0,
    show_rfx_price: true,
    reverse_auction: false,
    rfx_portal_text: DEFAULT_PORTAL_TEXT,
    notify_rfx_suppliers: true,
    rfx_enquiry_email: '',
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/rfq-settings', settings);
      toast.success('RFx settings saved');
    } catch { toast.error('Failed to save settings'); }
    setSaving(false);
  };

  const publicPortalUrl = `${window.location.origin}/public_RFX`;

  if (loading || !settings) return <LoadingSpinner />;

  return (
    <div data-testid="rfx-settings-panel">
      <h1 className="text-2xl font-bold mb-1">RFx Sourcing</h1>
      <p className="text-lg font-medium text-muted-foreground mb-6">RFx Settings</p>

      {/* ── Section 1: RFx Required Limit ── */}
      <div className="border rounded-lg p-6 mb-6">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">1</span>
          Set RFx Required Limit
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          The RFx Required Limit is the purchase amount threshold above which a competitive sourcing exercise (RFI, RFQ, RFP, or Reverse Auction) becomes mandatory.
          When a requisition exceeds this limit, the "Create Order" button is disabled — the user must run an RFx sourcing event first, and a purchase order can only be generated after the event is awarded.
          If the requisition amount is below this limit, the user can either create an RFx event or go directly to a purchase order.
        </p>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Label className="w-48 text-sm">Activate RFx Limit</Label>
            <div className="flex items-center gap-2">
              <Switch checked={settings.activate_rfx_limit} onCheckedChange={v => setSettings(p => ({ ...p, activate_rfx_limit: v }))} />
              <span className={`text-sm font-medium ${settings.activate_rfx_limit ? 'text-green-600' : 'text-muted-foreground'}`}>
                {settings.activate_rfx_limit ? 'Yes — RFx required above limit' : 'No — Direct orders always allowed'}
              </span>
            </div>
          </div>
          {settings.activate_rfx_limit && (
            <div className="flex items-center gap-4">
              <Label className="w-48 text-sm">RFx Required Limit ($)</Label>
              <Input type="number" value={settings.rfx_required_limit || 0}
                onChange={e => setSettings(p => ({ ...p, rfx_required_limit: parseFloat(e.target.value) || 0 }))}
                className="max-w-xs" placeholder="e.g. 10000" />
              <span className="text-xs text-muted-foreground">Requisitions above this value must go through RFx sourcing</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Section 2: RFx Settings ── */}
      <div className="border rounded-lg p-6 mb-6">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">2</span>
          RFx Settings
        </h3>
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <Label className="w-48 text-sm pt-1">Show / Hide Supplier Prices</Label>
            <div>
              <div className="flex items-center gap-2">
                <Switch checked={settings.show_rfx_price !== false} onCheckedChange={v => setSettings(p => ({ ...p, show_rfx_price: v }))} />
                <span className={`text-sm font-medium ${settings.show_rfx_price !== false ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {settings.show_rfx_price !== false ? 'Yes — Prices visible to evaluators' : 'No — Prices hidden until closing date'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                When set to No, supplier pricing on RFQ and RFP responses is hidden from evaluators until after the event closing date has passed. This is useful for sealed-bid evaluations where you want the technical evaluation to be completed before commercial information is revealed.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 border-t pt-4">
            <Label className="w-48 text-sm pt-1">Enable Reverse Auctions</Label>
            <div className="flex items-center gap-2">
              <Switch checked={settings.reverse_auction} onCheckedChange={v => setSettings(p => ({ ...p, reverse_auction: v }))} />
              <span className={`text-sm font-medium ${settings.reverse_auction ? 'text-green-600' : 'text-muted-foreground'}`}>
                {settings.reverse_auction ? 'Yes' : 'No'}
              </span>
            </div>
          </div>

          <div className="flex items-start gap-4 border-t pt-4">
            <Label className="w-48 text-sm pt-1">Public RFx Portal URL</Label>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Input value={publicPortalUrl} readOnly className="max-w-md bg-slate-50 text-slate-600" />
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(publicPortalUrl); toast.success('URL copied'); }}>
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                </Button>
                <Button size="sm" variant="outline" onClick={() => window.open(publicPortalUrl, '_blank')}>
                  <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">This is the public-facing URL where suppliers can browse and respond to your sourcing events. Share this with potential suppliers.</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Section 3: RFx Text & Email Notifications ── */}
      <div className="border rounded-lg p-6 mb-6">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">3</span>
          RFx Text &amp; Email Notifications
        </h3>

        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Label className="text-sm font-medium">Public RFx Portal Instructions</Label>
              <div className="relative group">
                <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                <div className="absolute left-0 top-full mt-1 z-50 w-72 p-3 bg-slate-900 text-white text-[11px] rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none leading-relaxed">
                  This text is displayed at the top of the public RFx portal. Use the toolbar to format text, add bold, links, and lists. The contact email is appended automatically from Section 4.
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-2">
              The portal instructions have two parts: a <strong>fixed glossary line</strong> (explaining RFP, RFQ, RFI, and Reverse Auction with hover tooltips — not editable) followed by <strong>your custom text</strong> below. Edit the custom text here. The supplier enquiry email (Section 4) is automatically appended at the bottom.
            </p>
            <div className="border rounded-lg overflow-hidden bg-white">
              <RichTextToolbar editor={editor} />
              <EditorContent editor={editor} className="prose prose-sm max-w-none p-4 min-h-[180px] focus:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[160px] [&_a]:text-indigo-700 [&_a]:underline" />
            </div>
            <Button size="sm" variant="ghost" className="mt-1 text-xs" onClick={() => { setSettings(p => ({ ...p, rfx_portal_text: DEFAULT_PORTAL_TEXT })); editor?.commands.setContent(DEFAULT_PORTAL_TEXT); }}>
              Reset to Default Text
            </Button>
          </div>

          <div className="flex items-start gap-4 border-t pt-4">
            <Label className="w-48 text-sm pt-1">Notify RFx Suppliers</Label>
            <div>
              <div className="flex items-center gap-2">
                <Switch checked={settings.notify_rfx_suppliers !== false} onCheckedChange={v => setSettings(p => ({ ...p, notify_rfx_suppliers: v }))} />
                <span className={`text-sm font-medium ${settings.notify_rfx_suppliers !== false ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {settings.notify_rfx_suppliers !== false ? 'Yes — Suppliers receive email notifications' : 'No — Email notifications disabled'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                When enabled, suppliers receive automated email notifications from the RFx system including: invitation emails, submission confirmations, clarification answers, document update alerts, and award/non-award notifications. When disabled, no RFx-related emails are sent to suppliers.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Section 4: Supplier Enquiries Contact ── */}
      <div className="border rounded-lg p-6 mb-6">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">4</span>
          Supplier Enquiries Contact
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          This email address is displayed on the public RFx portal as the contact point for suppliers who have
          questions or difficulties. It appears at the bottom of the instructions panel.
        </p>
        <div className="flex items-center gap-4">
          <Label className="w-48 text-sm">Supplier Enquiries Email</Label>
          <Input value={settings.rfx_enquiry_email || ''} onChange={e => setSettings(p => ({ ...p, rfx_enquiry_email: e.target.value }))}
            placeholder="rfx-enquiries@yourcompany.com" className="max-w-md" type="email" />
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          <Save className="h-4 w-4 mr-1" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
